import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const BrickCalculator = () => {
  const [brickData, setBrickData] = useState({
    length: '',
    width: '',
    height: '',
    wallArea: '',
    brickVolume: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBrickData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateBricks = () => {
    const { length, width, height, wallArea } = brickData;
    
    const len = parseFloat(length) || 0;
    const wid = parseFloat(width) || 0;
    const hei = parseFloat(height) || 0;
    const area = parseFloat(wallArea) || 0;

    if (len <= 0 || isNaN(len)) {
      setError('Please enter a valid length');
      return;
    }

    if (wid <= 0 || isNaN(wid)) {
      setError('Please enter a valid width');
      return;
    }

    if (hei <= 0 || isNaN(hei)) {
      setError('Please enter a valid height');
      return;
    }

    if (area <= 0 || isNaN(area)) {
      setError('Please enter a valid wall area');
      return;
    }

    // Calculate brick volume
    const brickVolume = len * wid * hei;

    // Calculate number of bricks needed
    const numberOfBricks = area / brickVolume;

    const calculationSteps = [
      'Step 1: Prepare the values',
      `Length: ${len} meters`,
      `Width: ${wid} meters`,
      `Height: ${hei} meters`,
      `Wall Area: ${area} square meters`,
      '\nStep 2: Calculate brick volume',
      `Brick Volume = Length × Width × Height`,
      `Brick Volume = ${len} × ${wid} × ${hei} = ${brickVolume.toFixed(4)} cubic meters`,
      '\nStep 3: Calculate number of bricks needed',
      `Number of Bricks = Wall Area / Brick Volume`,
      `Number of Bricks = ${area} / ${brickVolume.toFixed(4)} = ${numberOfBricks.toFixed(2)} bricks`
    ];

    const newResults = {
      numberOfBricks: numberOfBricks.toFixed(2),
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      bar: {
        labels: ['Number of Bricks'],
        datasets: [{
          label: 'Number of Bricks',
          data: [numberOfBricks],
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2
        }]
      }
    });
  };

  const exampleData = {
    length: 0.2,
    width: 0.1,
    height: 0.06,
    wallArea: 10,
    numberOfBricks: 833.33
  };

  return (
    <>
     <Helmet>
      {/* Basic SEO Meta Tags */}
      <title>Brick Calculator - Construction Estimation</title>
      <meta name="description" content="Calculate the number of bricks required for construction projects. Use our interactive brick calculator to estimate the number of bricks needed." />
      <meta name="keywords" content="brick calculator, construction estimation, number of bricks calculation, brick volume calculation, construction projects" />
      <meta name="author" content="Your Company Name" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Open Graph Meta Tags for Facebook and LinkedIn */}
      <meta property="og:title" content="Brick Calculator - Construction Estimation" />
      <meta property="og:description" content="Calculate the number of bricks required for construction projects. Use our interactive brick calculator to estimate the number of bricks needed." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://yourdomain.com/brick-calculator" />
      <meta property="og:image" content="https://yourdomain.com/brick-calculator-preview.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:site_name" content="Construction Calculators" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@your_twitter_handle" />
      <meta name="twitter:creator" content="@your_twitter_handle" />
      <meta name="twitter:title" content="Brick Calculator - Construction Estimation" />
      <meta name="twitter:description" content="Calculate the number of bricks required for construction projects. Use our interactive brick calculator to estimate the number of bricks needed." />
      <meta name="twitter:image" content="https://yourdomain.com/brick-calculator-preview.jpg" />
      <meta name="twitter:image:alt" content="Brick Calculator Preview" />
      
      {/* Pinterest Meta Tags */}
      <meta name="pinterest" content="nopin" />
      <meta name="pinterest-rich-pin" content="true" />
      
      {/* Schema.org Markup for Google+ */}
      <meta itemprop="name" content="Brick Calculator - Construction Estimation" />
      <meta itemprop="description" content="Calculate the number of bricks required for construction projects. Use our interactive brick calculator to estimate the number of bricks needed." />
      <meta itemprop="image" content="https://yourdomain.com/brick-calculator-preview.jpg" />
      
      {/* Additional Meta Tags for Better Sharing */}
      <meta name="theme-color" content="#ffffff" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Brick Calculator" />
    </Helmet>

    <div className="brick-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🧱</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Brick Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Construction Estimation</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Calculate the number of bricks required for construction projects. This calculator helps you estimate the number of bricks needed for your project.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="length">
            Length of Brick (meters):
          </label>
          <input
            type="number"
            id="length"
            name="length"
            value={brickData.length}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter length in meters"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="width">
            Width of Brick (meters):
          </label>
          <input
            type="number"
            id="width"
            name="width"
            value={brickData.width}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter width in meters"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="height">
            Height of Brick (meters):
          </label>
          <input
            type="number"
            id="height"
            name="height"
            value={brickData.height}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter height in meters"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="wallArea">
            Wall Area (square meters):
          </label>
          <input
            type="number"
            id="wallArea"
            name="wallArea"
            value={brickData.wallArea}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter wall area in square meters"
          />
        </div>

        <button
          onClick={calculateBricks}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Number of Bricks
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Brick Calculation</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Number of Bricks</span>
                    <span className="font-medium">{results.numberOfBricks}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Number of Bricks'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Measurement'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Brick Calculation'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation for a brick with dimensions 0.2 meters (length) × 0.1 meters (width) × 0.06 meters (height) and a wall area of 10 square meters:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Length: {exampleData.length} meters</li>
              <li>Width: {exampleData.width} meters</li>
              <li>Height: {exampleData.height} meters</li>
              <li>Wall Area: {exampleData.wallArea} square meters</li>
              <li>Number of Bricks: {exampleData.numberOfBricks} bricks</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Calculate brick volume:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Brick Volume = Length × Width × Height</li>
                  <li>Brick Volume = {exampleData.length} × {exampleData.width} × {exampleData.height} = {exampleData.numberOfBricks / exampleData.wallArea} cubic meters</li>
                </ul>
              </li>
              <li>
                <p>Calculate number of bricks needed:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Number of Bricks = Wall Area / Brick Volume</li>
                  <li>Number of Bricks = {exampleData.wallArea} / ({exampleData.numberOfBricks / exampleData.wallArea}) = {exampleData.numberOfBricks} bricks</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Brick Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Brick Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Project Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Measure the length, width, and height of the brick accurately</li>
                <li>Measure the wall area accurately</li>
                <li>Ensure all measurements are precise</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Project Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the length, width, and height of the brick in meters</li>
                <li>Input the wall area in square meters</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the calculated number of bricks</li>
                <li>Use the bar chart for visual analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-6">
            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What is the formula to calculate the number of bricks?</h3>
              <p className="text-gray-600">
                The formula to calculate the number of bricks is: Number of Bricks = Wall Area / Brick Volume
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why is it important to calculate the number of bricks accurately?</h3>
              <p className="text-gray-600">
                Accurate calculation of the number of bricks is important because it:
                <ul className="list-disc pl-6 mt-2">
                  <li>Ensures you order the correct number of bricks</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Can I use this calculator for other types of construction projects?</h3>
              <p className="text-gray-600">
                Yes, this calculator can be used for other construction projects where you need to calculate the number of bricks required, such as for garden walls, patios, and more.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Visual Analysis</h3>
              <p className="text-gray-600">Interactive bar charts for easy visualization of brick calculations</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Precise Calculations</h3>
              <p className="text-gray-600">Accurate brick quantity calculations with step-by-step breakdown</p>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Unit Conversion</h3>
              <p className="text-gray-600">Automatic handling of measurement units</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
              <p className="text-gray-600">Detailed explanations of brick calculation concepts</p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-lg">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-lg font-semibold mb-2">User-Friendly</h3>
              <p className="text-gray-600">Intuitive interface with clear input fields</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
              <p className="text-gray-600">Instant calculation results as you type</p>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-xl font-semibold mb-4">Key Benefits</h3>
            <ul className="list-disc pl-8 space-y-2">
              <li>Save time with automatic calculations</li>
              <li>Reduce calculation errors with built-in validation</li>
              <li>Learn brick calculation concepts through practical examples</li>
              <li>Accessible from any device</li>
              <li>Free to use for educational and professional purposes</li>
              <li>Perfect for construction professionals and DIY enthusiasts</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Brick Calculations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Calculating the number of bricks is essential for planning construction projects and ensuring the correct amount of material is ordered. Here's why it's important:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Accurate Material Estimation:</span> Ensuring you order the correct number of bricks
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Cost Efficiency:</span> Avoiding over-purchasing and minimizing waste
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Project Management:</span> Planning and scheduling construction effectively
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Structural Integrity:</span> Ensuring the correct quantity for the required strength and support
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Always double-check your measurements and calculations to ensure accuracy and avoid costly mistakes.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default BrickCalculator;